import { Divider, HStack, Icon, useDisclosure } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import CDS from "design-system";
import { useQuery } from "@tanstack/react-query";
import EventsTable from "./EventsTable";
import { fetchPlatformEvents } from "../api";
import { platformEventKeys } from "../hooks";
import type { EventTypePrefix, PlatformEventFilters } from "../types";

interface CollapsibleHistoryProps {
  entityId: string;
  entityType: string;
  eventType: EventTypePrefix;
  showNewHireReference?: boolean;
  withDivider?: boolean;
}

export default function CollapsibleHistory({
  entityId,
  entityType,
  eventType,
  showNewHireReference = true,
  withDivider = true,
}: CollapsibleHistoryProps) {
  const { isOpen, onToggle } = useDisclosure();

  const { data: countResponse } = useQuery({
    queryKey: [...platformEventKeys.lists(), "count", entityType, entityId],
    queryFn: ({ signal }) =>
      fetchPlatformEvents(
        `?event_type=${eventType}&entity_type=${entityType}&entity_id=${entityId}&pagination[page]=1&pagination[per_page]=1`,
        signal,
      ),
    enabled: !!entityId,
    staleTime: 1000 * 60 * 3,
  });

  const count = countResponse?.meta?.pagination?.count ?? null;
  const label = count != null
    ? `History (${count} ${count === 1 ? "item" : "items"})`
    : "History";

  const filters: PlatformEventFilters = {
    eventType,
    entityType,
    entityId,
    page: 1,
    perPage: 10,
    sortBy: "occurred_at",
    sortOrder: "desc",
  };

  return (
    <>
      {withDivider && <Divider my="6" />}
      <HStack
        spacing={1}
        align="center"
        mb={isOpen ? 3 : 0}
        cursor="pointer"
        onClick={onToggle}
        display="inline-flex"
        _hover={{ opacity: 0.7 }}
      >
        <Icon
          as={isOpen ? ChevronDownIcon : ChevronRightIcon}
          boxSize={4}
          color="fg.subtle"
        />
        <CDS.Text12 fontWeight="semibold" color="fg.default">
          {label}
        </CDS.Text12>
      </HStack>
      {isOpen && (
        <EventsTable
          filters={filters}
          onFiltersChange={() => {}}
          isCompact
          showNewHireReference={showNewHireReference}
        />
      )}
    </>
  );
}
